import { useState, useEffect, useCallback } from 'react';
import { hospitalApi } from '../../hospitalApi';

const C = '#1b6ca8';
const DARK = '#0f4c75';
const LIGHT = '#e8f4fd';

const today = () => new Date().toISOString().split('T')[0];

const STATUS_COLOR = {
  waiting:    { bg: '#fef3c7', color: '#f9ab00' },
  consulting: { bg: '#e8f4fd', color: C },
  completed:  { bg: '#e6f4ea', color: '#34a853' },
  'no-show':  { bg: '#f1f3f4', color: '#9aa0a6' },
};

const STATUS_OPTS = ['waiting', 'consulting', 'completed', 'no-show'];

const STATUS_NEXT = {
  waiting: 'consulting',
  consulting: 'completed',
};

const STATUS_NEXT_LABEL = {
  waiting: 'Start',
  consulting: 'Complete',
};

const EMPTY_FORM = {
  patientType: 'existing',
  patientId: '',
  walkInName: '',
  walkInPhone: '',
  doctorId: '',
  visitDate: today(),
  chiefComplaint: '',
  diagnosis: '',
  prescriptionNotes: '',
  fee: '',
  status: 'waiting',
};

const inp = { className: 'form-control' };

function F({ label, children, span }) {
  return (
    <div className="form-group" style={span ? { gridColumn: '1/-1' } : {}}>
      <label className="form-label">{label}</label>
      {children}
    </div>
  );
}

function Modal({ title, onClose, children, width }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 2000,
      display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: width || 540,
        maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 17, color: DARK }}>{title}</div>
          <button style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#5f6368' }}
            onClick={onClose}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ErrBanner({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ color: '#c5221f', background: '#fce8e6', borderRadius: 8, padding: '8px 12px',
      marginBottom: 14, fontSize: 13 }}>
      {msg}
    </div>
  );
}

function VisitForm({ form, setForm, patients, doctors }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
      <div className="form-group" style={{ gridColumn: '1/-1' }}>
        <label className="form-label">Patient Type</label>
        <div style={{ display: 'flex', gap: 6 }}>
          {['existing', 'walk-in'].map(t => (
            <button key={t} onClick={() => setForm(f => ({ ...f, patientType: t, patientId: '', walkInName: '', walkInPhone: '' }))}
              style={{
                padding: '7px 20px', borderRadius: 7, border: 'none', cursor: 'pointer',
                fontWeight: 600, fontSize: 13,
                background: form.patientType === t ? C : '#f1f3f4',
                color: form.patientType === t ? '#fff' : '#5f6368',
              }}>
              {t === 'existing' ? 'Existing Patient' : 'Walk-in'}
            </button>
          ))}
        </div>
      </div>

      {form.patientType === 'existing' ? (
        <F label="Patient *" span>
          <select {...inp} value={form.patientId} onChange={e => setForm(f => ({ ...f, patientId: e.target.value }))}>
            <option value="">Select Patient</option>
            {patients.map(p => <option key={p.id} value={String(p.id)}>{p.name}{p.phone ? ` — ${p.phone}` : ''}</option>)}
          </select>
        </F>
      ) : (
        <>
          <F label="Name *">
            <input {...inp} value={form.walkInName}
              onChange={e => setForm(f => ({ ...f, walkInName: e.target.value }))} placeholder="Walk-in patient name" />
          </F>
          <F label="Phone">
            <input {...inp} value={form.walkInPhone}
              onChange={e => setForm(f => ({ ...f, walkInPhone: e.target.value }))} placeholder="Phone number" />
          </F>
        </>
      )}

      <F label="Doctor *">
        <select {...inp} value={form.doctorId} onChange={e => setForm(f => ({ ...f, doctorId: e.target.value }))}>
          <option value="">Select Doctor</option>
          {doctors.map(d => <option key={d.id} value={String(d.id)}>Dr. {d.name}{d.specialization ? ` (${d.specialization})` : ''}</option>)}
        </select>
      </F>

      <F label="Visit Date">
        <input {...inp} type="date" value={form.visitDate}
          onChange={e => setForm(f => ({ ...f, visitDate: e.target.value }))} />
      </F>

      <F label="Chief Complaint" span>
        <textarea {...inp} rows={2} value={form.chiefComplaint}
          onChange={e => setForm(f => ({ ...f, chiefComplaint: e.target.value }))}
          style={{ resize: 'vertical' }} placeholder="Reason for visit" />
      </F>

      <F label="Diagnosis" span>
        <textarea {...inp} rows={2} value={form.diagnosis}
          onChange={e => setForm(f => ({ ...f, diagnosis: e.target.value }))}
          style={{ resize: 'vertical' }} placeholder="Optional" />
      </F>

      <F label="Prescription Notes" span>
        <textarea {...inp} rows={2} value={form.prescriptionNotes}
          onChange={e => setForm(f => ({ ...f, prescriptionNotes: e.target.value }))}
          style={{ resize: 'vertical' }} placeholder="Optional" />
      </F>

      <F label="Fee (₹)">
        <input {...inp} type="number" min="0" value={form.fee}
          onChange={e => setForm(f => ({ ...f, fee: e.target.value }))} />
      </F>

      <F label="Status">
        <select {...inp} value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
          {STATUS_OPTS.map(s => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1).replace('-', ' ')}</option>
          ))}
        </select>
      </F>
    </div>
  );
}

export default function HospitalOPD({ role }) {
  const [date, setDate]           = useState(today());
  const [visits, setVisits]       = useState([]);
  const [patients, setPatients]   = useState([]);
  const [doctors, setDoctors]     = useState([]);
  const [filterDoc, setFilterDoc] = useState('');
  const [filterSt, setFilterSt]   = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [editId, setEditId]       = useState(null);
  const [saving, setSaving]       = useState(false);
  const [err, setErr]             = useState('');

  const load = useCallback(() => {
    hospitalApi.getOpdVisits(date).then(d => { if (d) setVisits(d); });
  }, [date]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    hospitalApi.getPatients().then(d => { if (d) setPatients(d); });
    hospitalApi.getDoctors().then(d => { if (d) setDoctors(d.filter(doc => doc.status === 'active')); });
  }, []);

  const filtered = visits.filter(v => {
    const docMatch = !filterDoc || String(v.doctor_id) === filterDoc;
    const stMatch = filterSt === 'all' || v.status === filterSt;
    return docMatch && stMatch;
  });

  const counts = STATUS_OPTS.reduce((acc, s) => {
    acc[s] = visits.filter(v => v.status === s).length;
    return acc;
  }, {});

  const resetForm = () => { setForm({ ...EMPTY_FORM, visitDate: date }); setEditId(null); setErr(''); };

  const openAdd = () => { resetForm(); setShowModal(true); };

  const openEdit = (v) => {
    setForm({
      patientType: v.patient_id ? 'existing' : 'walk-in',
      patientId: v.patient_id ? String(v.patient_id) : '',
      walkInName: v.walk_in_name || '',
      walkInPhone: v.walk_in_phone || '',
      doctorId: v.doctor_id ? String(v.doctor_id) : '',
      visitDate: v.visit_date || date,
      chiefComplaint: v.chief_complaint || '',
      diagnosis: v.diagnosis || '',
      prescriptionNotes: v.prescription_notes || '',
      fee: v.fee != null ? String(v.fee) : '',
      status: v.status || 'waiting',
    });
    setEditId(v.id);
    setShowModal(true);
    setErr('');
  };

  const handleSave = async () => {
    const needsPatient = form.patientType === 'existing' ? !form.patientId : !form.walkInName.trim();
    if (needsPatient || !form.doctorId) {
      setErr('Patient and Doctor are required');
      return;
    }
    setSaving(true); setErr('');
    try {
      const payload = {
        ...form,
        patientId: form.patientType === 'existing' ? parseInt(form.patientId) : null,
        doctorId: parseInt(form.doctorId),
        fee: form.fee !== '' ? parseFloat(form.fee) : null,
      };
      if (editId) await hospitalApi.updateOpdVisit(editId, payload);
      else await hospitalApi.createOpdVisit(payload);
      load();
      setShowModal(false);
      resetForm();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const advanceStatus = async (v) => {
    const next = STATUS_NEXT[v.status];
    if (!next) return;
    await hospitalApi.updateOpdVisit(v.id, { ...v, status: next, patientId: v.patient_id, doctorId: v.doctor_id });
    load();
  };

  const SUMMARY_CONFIG = [
    { key: 'waiting',    icon: '🟡' },
    { key: 'consulting', icon: '🔵' },
    { key: 'completed',  icon: '✅' },
    { key: 'no-show',    icon: '⬜' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="form-control" type="date" value={date}
          onChange={e => setDate(e.target.value)}
          style={{ width: 160 }} />

        <select className="form-control" value={filterDoc} onChange={e => setFilterDoc(e.target.value)}
          style={{ width: 200 }}>
          <option value="">All Doctors</option>
          {doctors.map(d => <option key={d.id} value={String(d.id)}>Dr. {d.name}</option>)}
        </select>

        <div style={{ display: 'flex', gap: 4 }}>
          <button onClick={() => setFilterSt('all')} style={{
            padding: '6px 14px', borderRadius: 7, border: 'none', cursor: 'pointer',
            fontWeight: 600, fontSize: 12,
            background: filterSt === 'all' ? C : '#f1f3f4',
            color: filterSt === 'all' ? '#fff' : '#5f6368',
          }}>All</button>
          {STATUS_OPTS.map(s => (
            <button key={s} onClick={() => setFilterSt(s)} style={{
              padding: '6px 14px', borderRadius: 7, border: 'none', cursor: 'pointer',
              fontWeight: 600, fontSize: 12, textTransform: 'capitalize',
              background: filterSt === s ? STATUS_COLOR[s].color : '#f1f3f4',
              color: filterSt === s ? '#fff' : '#5f6368',
            }}>
              {s.replace('-', ' ')}
            </button>
          ))}
        </div>

        {role !== 'viewer' && (
          <button className="btn btn-primary" style={{ marginLeft: 'auto' }} onClick={openAdd}>
            + Register Patient
          </button>
        )}
      </div>

      <div style={{ background: '#fff', borderRadius: 10, padding: '12px 18px',
        boxShadow: '0 1px 6px rgba(0,0,0,.07)', marginBottom: 14,
        display: 'flex', gap: 28, flexWrap: 'wrap' }}>
        {SUMMARY_CONFIG.map(s => (
          <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <span>{s.icon}</span>
            <span style={{ fontWeight: 700, color: DARK }}>{counts[s.key]}</span>
            <span style={{ color: '#5f6368', textTransform: 'capitalize' }}>{s.key.replace('-', ' ')}</span>
          </div>
        ))}
        <div style={{ marginLeft: 'auto', fontSize: 12, color: '#9aa0a6', alignSelf: 'center' }}>
          {visits.length} total visits · {date}
        </div>
      </div>

      <div className="card">
        <div className="card-body" style={{ padding: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: LIGHT }}>
                {['Token #', 'Patient', 'Doctor', 'Chief Complaint', 'Diagnosis', 'Fee', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: DARK, fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ padding: 32, textAlign: 'center', color: '#9aa0a6' }}>
                    No OPD visits for this date
                  </td>
                </tr>
              ) : filtered.map((v, i) => {
                const sc = STATUS_COLOR[v.status] || { bg: '#f1f3f4', color: '#5f6368' };
                const patientDisplay = v.patient_name || v.walk_in_name || 'Walk-in';
                const nextStatus = STATUS_NEXT[v.status];
                return (
                  <tr key={v.id} style={{ borderTop: '1px solid #f1f3f4', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                    <td style={{ padding: '9px 12px', fontWeight: 700, color: DARK }}>
                      {v.token_no || `#${i + 1}`}
                    </td>
                    <td style={{ padding: '9px 12px' }}>
                      <div style={{ fontWeight: 600, color: DARK }}>{patientDisplay}</div>
                      {!v.patient_id && v.walk_in_phone && (
                        <div style={{ fontSize: 11, color: '#9aa0a6' }}>{v.walk_in_phone}</div>
                      )}
                    </td>
                    <td style={{ padding: '9px 12px', color: '#5f6368' }}>
                      {v.doctor_name ? `Dr. ${v.doctor_name}` : '—'}
                    </td>
                    <td style={{ padding: '9px 12px', color: '#5f6368', maxWidth: 160 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {v.chief_complaint || '—'}
                      </div>
                    </td>
                    <td style={{ padding: '9px 12px', color: '#5f6368', maxWidth: 160 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {v.diagnosis || '—'}
                      </div>
                    </td>
                    <td style={{ padding: '9px 12px', fontWeight: 600, color: DARK }}>
                      {v.fee != null ? '₹' + Number(v.fee).toLocaleString('en-IN') : '—'}
                    </td>
                    <td style={{ padding: '9px 12px' }}>
                      <span style={{ background: sc.bg, color: sc.color, borderRadius: 6,
                        padding: '2px 8px', fontSize: 11, fontWeight: 700, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                        {v.status.replace('-', ' ')}
                      </span>
                    </td>
                    <td style={{ padding: '9px 12px' }}>
                      <div style={{ display: 'flex', gap: 5 }}>
                        {role !== 'viewer' && nextStatus && (
                          <button style={{
                            fontSize: 11, padding: '3px 9px', borderRadius: 5, cursor: 'pointer',
                            fontWeight: 600, border: 'none',
                            background: STATUS_COLOR[nextStatus]?.bg || LIGHT,
                            color: STATUS_COLOR[nextStatus]?.color || DARK,
                          }}
                            onClick={() => advanceStatus(v)}>
                            {STATUS_NEXT_LABEL[v.status]} &rsaquo;
                          </button>
                        )}
                        {role !== 'viewer' && (
                          <button style={{ fontSize: 11, padding: '3px 9px', background: LIGHT,
                            color: DARK, border: `1px solid ${C}`, borderRadius: 5, cursor: 'pointer', fontWeight: 600 }}
                            onClick={() => openEdit(v)}>
                            Edit
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <Modal title={editId ? 'Edit OPD Visit' : 'Register Patient'} onClose={() => { setShowModal(false); resetForm(); }}>
          <ErrBanner msg={err} />
          <VisitForm form={form} setForm={setForm} patients={patients} doctors={doctors} />
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : editId ? 'Update Visit' : 'Register'}
            </button>
            <button style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #e8eaed',
              background: '#fff', cursor: 'pointer', fontSize: 13 }}
              onClick={() => { setShowModal(false); resetForm(); }}>Cancel</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
